import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  InsertScrewFamilyBody,
  ScrewFamily,
  SearchScrewFamiliesResponse,
  UpdateScrewFamilyBody,
} from "./types/screwFamily";

const brScrewFamiliesUrl = "/admin/modules/beam_reinforcement/screw_families";

export const useBRScrewFamiliesApi = () => {
  const api = useApi();

  const useBRScrewFamilies = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(brScrewFamiliesUrl, query);
      return useSWR<SearchScrewFamiliesResponse, CustomError, string>(url, (requestUrl) =>
        api.get<SearchScrewFamiliesResponse>({ url: requestUrl, signal }),
      );
    },
    [api],
  );

  const getBRScrewFamily = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<ScrewFamily, CustomError, string>(`${brScrewFamiliesUrl}/${id}`, (url) =>
        api.get<ScrewFamily>({ url, signal }),
      );
    },
    [api],
  );

  const insertBRScrewFamily = useCallback(
    async (body: InsertScrewFamilyBody): Promise<{ id: string }> => {
      const result = await api.post<{ id: string }, InsertScrewFamilyBody>(brScrewFamiliesUrl, body);
      mutateCache([brScrewFamiliesUrl]);
      return result;
    },
    [api],
  );

  const updateBRScrewFamily = useCallback(
    async (id: string, body: UpdateScrewFamilyBody): Promise<void> => {
      const url = `${brScrewFamiliesUrl}/${id}`;
      await api.put<UpdateScrewFamilyBody>(url, body);
      mutateCache([brScrewFamiliesUrl], url);
    },
    [api],
  );

  const deleteBRScrewFamily = useCallback(
    async (id: string): Promise<void> => {
      const url = `${brScrewFamiliesUrl}/${id}`;
      await api.del(url);
      mutateCache([brScrewFamiliesUrl], url);
    },
    [api],
  );

  const updateBRScrewFamilyVisibility = useCallback(
    async (id: string, body: { visibility: string }): Promise<void> => {
      const url = `${brScrewFamiliesUrl}/${id}/visibility`;
      await api.put<{ visibility: string }>(url, body);
      mutateCache([brScrewFamiliesUrl], url);
    },
    [api],
  );

  const updateBRScrewFamilyOrder = useCallback(
    async (id: string, body: { order: number }): Promise<void> => {
      const url = `${brScrewFamiliesUrl}/${id}/order`;
      await api.put<{ order: number }>(url, body);
      mutateCache([brScrewFamiliesUrl], url);
    },
    [api],
  );

  return {
    useBRScrewFamilies,
    getBRScrewFamily,
    insertBRScrewFamily,
    updateBRScrewFamily,
    deleteBRScrewFamily,
    updateBRScrewFamilyVisibility,
    updateBRScrewFamilyOrder,
  };
};

export default useBRScrewFamiliesApi;
